import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Table, InputNumber, Select, Button,
  Breadcrumb,
  Space,
  Flex,
  DatePicker,
} from 'antd';
import { HomeOutlined } from '@ant-design/icons';

import { apiRequest } from '../../../../service/apiRequest';
import { Wood } from "../../../../types/Wood";
import { showSuccess, showError } from '../../../../components/errorHandler';
const { Option } = Select;


type WoodRow = {
  key: string;
  woodId: number | null;
  widths: { [width: number]: number };
  rate: number;
};

const woodWidths = Array.from({ length: 18 }, (_, i) => i + 3); // 3 to 20 inches

const PartyNapi: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [partyName, setPartyName] = useState('');
  const [challan, setChallan] = useState('');
  const [date, setDate] = useState('');
  const [dataSource, setDataSource] = useState<WoodRow[]>([
    {
      key: '1',
      woodId: null,
      widths: woodWidths.reduce((acc, w) => ({ ...acc, [w]: 0 }), {}),
      rate: 500,
    },
  ]);


  const [wooddata, setWoodData] = useState<Wood[]>([]);

  // Add new row
  const handleAddRow = () => {
    const newKey = (dataSource.length + 1).toString();
    setDataSource([
      ...dataSource,
      {
        key: newKey,
        woodId: null,
        widths: woodWidths.reduce((acc, w) => ({ ...acc, [w]: 0 }), {}),
        rate: 500,
      },
    ]);
  };

  // Handle wood size change
  const handleWoodSizeChange = (value: number | null, key: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex(item => item.key === key);
    if (index > -1) {
      newData[index].woodId = value;
      setDataSource(newData);
    }
  };

  // Handle width count change
  const handleWidthChange = (value: number | null, key: string, width: number) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => item.key === key);
    if (index > -1) {
      newData[index].widths[width] = value || 0;
      setDataSource(newData);
    }
  };

  // Handle rate change
  const handleRateChange = (value: number | null, key: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => item.key === key);
    if (index > -1) {
      newData[index].rate = value || 0;
      setDataSource(newData);
    }
  };

  // Calculate total pieces
  const calculateTotalPcs = (widths: { [width: number]: number }) =>
    Object.values(widths).reduce((acc, val) => acc + val, 0);

  const getWoodById = (woodId: number | null, woodData: Wood[]) =>
    woodData.find(w => w.wood_id === woodId);
  // Calculate CFT
  const calculateCFT = (
    woodId: number | null,
    widths: { [width: number]: number },
    woodData: Wood[],
  ) => {
    if (!woodId) return 0;
    const wood = getWoodById(woodId, woodData);
    if (!wood) return 0;

    const totalPcs = Object.values(widths).reduce((sum, val) => sum + val, 0);

    // Safely get length and thickness as numbers (default to 0)
    const length = wood?.wood_length ?? 0;
    const thickness = wood?.wood_thickness ?? 0;

    const cft = (totalPcs * (length * thickness)) / 144;

    return parseFloat(cft.toFixed(3));
  };



  // Calculate amount
  const calculateAmount = (cft: number, rate: number) => parseFloat((cft * rate).toFixed(2));



  const handleSubmit = async () => {
    // Prepare the payload in the expected format by your backend
    const formData = {
      partyName,
      challan,
      date,
      woodDetails: dataSource.map(row => ({
        woodId: row.woodId,
        widths: row.widths,
        rate: row.rate,
      })),
    };

    try {
      const response = await apiRequest({
        method: 'post',          // or 'put' depending on your API
        url: 'party-napi',  // your API endpoint for submitting purchase orders
        data: formData,
      });

      showSuccess('Data submitted successfully!');
      console.log('Submitted Purchase Order:', response);
    } catch (error) {
      showError('Failed to submit data');
      console.error(error);
    }
  };



  // Define columns
  const columns = [
    {
      title: 'Wood Size',
      dataIndex: 'woodSize',
      key: 'woodSize',
      fixed: 'left' as const,
      width: 120,
      render: (_: any, record: WoodRow) => (
        <Select
          value={record.woodId}
          onChange={(value) => handleWoodSizeChange(value, record.key)}
          placeholder="Select wood"
          options={wooddata.map(w => ({ label: w.wood_name, value: w.wood_id }))}
          allowClear
        />
      ),
    },
    ...woodWidths.map((width) => ({
      title: `${width}"`,
      dataIndex: `width-${width}`,
      key: `width-${width}`,
      width: 80,
      render: (_: any, record: WoodRow) => (
        <InputNumber
          min={0}
          value={record.widths[width]}
          onChange={(val) => handleWidthChange(val, record.key, width)}
          style={{ width: '70px' }}
        />
      ),
    })),
    {
      title: 'Total Pcs',
      key: 'totalPcs',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => calculateTotalPcs(record.widths),
    },
    {
      title: 'CFT',
      key: 'cft',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => calculateCFT(record.woodId, record.widths, wooddata),
    },
    {
      title: 'Rate',
      key: 'rate',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => (
        <InputNumber
          min={0}
          value={record.rate}
          onChange={(val) => handleRateChange(val, record.key)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: WoodRow) => {
        const cft = calculateCFT(record.woodId, record.widths, wooddata);
        return calculateAmount(cft, record.rate);
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right' as const,
      width: 80,
      render: (_: any, record: WoodRow, index: number) =>
        index === dataSource.length - 1 ? (
          <Button type="primary" onClick={handleAddRow}>
            +
          </Button>
        ) : null,
    },
  ];



  const fetchWood = async (page = 1, limit = 10) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      const data = await apiRequest<Wood[]>({
        method: 'get',
        url: `woods?${params.toString()}`,
      });
      setWoodData(data);
    } catch (error) {
      showError('Failed to fetch Wood');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchWood();
  }, []);
  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Party NAPI</Breadcrumb.Item>
        </Breadcrumb>



      </Space>
      <div style={{ padding: 20 }}>
        <h2>Purchase Order</h2>

        <div style={{ marginBottom: 16 }}>
          <label>Party Name: Ajeeet Panwar and party</label>

        </div>

        <div style={{ marginBottom: 16 }}>
          <label>Challan: 2455/CH</label>

        </div>

        <div style={{ marginBottom: 20 }}>
          <label>Date: </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ marginLeft: 10 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered
          size="small"
        />

        <div style={{ marginTop: 20 }}>
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </div>
      </div>

    </>
  );
};

export default PartyNapi;
